import { OmniElement, html, css, repeat, nothing, styleMap } from 'omni-ui';

import './campaign-audience-add-segments.js';
import './campaign-audience-segment-info.js';
import './campaign-audience-rfc-sliders.js';
import { rfcColors } from './campaign-audience-rfc-legend.js';

/**
 * Takes in audience size as number and returns a formatted string.
 * @param { number }
 * @returns { string }
 */
const formatAudienceNum = num => {
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  const tier = Math.floor(Math.log10(Math.abs(num)) / 3);

  if (tier === 0) {
    return num.toLocaleString();
  }

  const suffix = suffixes[tier];
  const scaled = num / 10 ** (tier * 3);

  const formattedNum = scaled.toFixed(1);

  return `${formattedNum} ${suffix}`;
};

export class CampaignAudienceClusterViz extends OmniElement {
  static get styles() {
    return [
      ...super.styles,
      css`
        :host,
        omni-style {
          height: 100%;
        }

        .omni .button.is-text omni-icon {
          --color-icon-lines: var(--color-almost-black);
          fill: var(--color-almost-black);
        }

        .cluster-container {
          height: 100%;
          width: 100%;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(12.07rem, 1fr));
          align-content: stretch;
          justify-items: center;
          gap: 1rem;
        }

        .container-child {
          width: 100%;
          display: flex;
          flex-flow: row nowrap;
          justify-content: center;
          align-items: center;
        }

        .circle {
          min-width: 12.07rem;
          max-width: 38rem;
          text-align: center;
          border: 1em solid var(--color-core-medium);
          border-radius: 50%;
          aspect-ratio: 1 / 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          animation: show-circles-above 0.3s ease-in;
          transition: all 0.05s ease-in;
        }

        .circle:nth-of-type(even) {
          animation: show-circles-below 0.3s ease-in;
        }

        .audience-title {
          font-weight: bold;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .audience-details {
          color: var(--color-shark);
          justify-self: center;
        }

        .audience-buttons {
          margin-top: 0.5em;
          display: none;
        }

        .circle:hover {
          background-color: var(--color-pale-grey);
          border: none;
          box-shadow:
            rgba(0, 0, 0, 0.3) 0rem 1rem 2rem,
            rgba(0, 0, 0, 0.22) 0rem 1rem 1rem;
          padding: 1.5em;
        }

        .circle:hover .audience-buttons {
          display: block;
        }

        @keyframes show-circles-above {
          0% {
            transform: translateY(50%) scale(0.5);
            opacity: 0.25;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes show-circles-below {
          0% {
            transform: translateY(-50%) scale(0.5);
            opacity: 0.1;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }
      `,
    ];
  }

  static get properties() {
    return {
      audiences: { type: Array },
      stages: { type: Array },
      audienceView: { type: String },
      audienceIndex: { type: Number },
      segmentInfoIsHidden: { type: Boolean },
      addSegmentIsHidden: { type: Boolean },
      rfcIsHidden: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.audiences = [];
    this.stages = [];
    this.selectedAEObject = [];
    this.audienceView = '';
    this.segmentInfoIsHidden = true;
    this.addSegmentIsHidden = true;
    this.rfcIsHidden = true;
  }

  #openSegmentInfo(audienceIndex) {
    this.audienceIndex = audienceIndex;
    this.segmentInfoIsHidden = false;
  }

  #closeSegmentInfo() {
    this.segmentInfoIsHidden = true;
    this.audienceIndex = 0;
  }

  #openAddSegment = audienceIndex => {
    this.audienceIndex = audienceIndex;
    this.addSegmentIsHidden = false;
  };

  #closeAddSegment() {
    this.addSegmentIsHidden = true;
    this.audienceIndex = 0;
  }

  #openRFC = audienceIndex => {
    this.audienceIndex = audienceIndex;
    this.rfcIsHidden = false;
  };

  #closeRFC() {
    this.rfcIsHidden = true;
    this.audienceIndex = 0;
  }

  get #addSegmentTemplate() {
    if (this.addSegmentIsHidden) return nothing;
    return html`
      <campaign-audience-add-segments
        .context=${this.context}
        .token=${this.token}
        .env=${this.env}
        .audience=${this.audiences[this.audienceIndex]}
        .stage=${this.stages[0]}
        .percentageIndex=${0}
        .audienceIndex=${this.audienceIndex}
        .selectedAEObject=${this.selectedAEObject}
        @close-add-segments=${this.#closeAddSegment}
      ></campaign-audience-add-segments>
    `;
  }

  get #segmentInfoTemplate() {
    if (this.segmentInfoIsHidden) return nothing;
    return html`
      <campaign-audience-segment-info
        .audience=${this.audiences[this.audienceIndex]}
        .stage=${this.stages[0]}
        .percentageIndex=${0}
        .audienceView=${this.audienceView}
        @close-segment-info=${this.#closeSegmentInfo}
      ></campaign-audience-segment-info>
    `;
  }

  get #rfcSlidersTemplate() {
    if (this.rfcIsHidden) return nothing;
    const audience = this.audiences[this.audienceIndex];
    const stage = this.stages[0];
    const { description, info, interest, need, role, title } =
      audience.stagePercentages[0].roleForComms;
    return html`
      <campaign-audience-rfc-sliders
        .audience=${audience}
        .stage=${stage}
        .percentageIndex=${0}
        .audienceIndex=${this.audienceIndex}
        .rfcDescription=${description}
        .info=${info}
        .interest=${interest}
        .need=${need}
        .rfc=${role}
        .rfcTitle=${title}
        @close-rfc-sliders=${this.#closeRFC}
      ></campaign-audience-rfc-sliders>
    `;
  }

  /**
   * Sorts audience data by size and renders a circle for each audience.
   * @param { number } The index of the audience data to render.
   * @returns html div element representing audience as a circle.
   */
  #renderCircle(audience, index) {
    const cellBtnClickHandler =
      this.audienceView === 'viz' ? this.#openAddSegment : this.#openRFC;

    const audiencePopulation = this.audiences.map(obj => obj.populationCount);
    const maxPopulation = Math.max(...audiencePopulation);
    const scaledPopulations = audiencePopulation.map(
      population => (population / maxPopulation) * 100,
    );
    const alignments = ['flex-start', 'flex-end', 'center'];
    const columnOpacity = [0.9, 0.7, 0.5, 0.3, 0.1];

    return html`
      <div class="container-child">
        <div
          class="circle"
          style=${styleMap({
            border:
              this.audienceView === 'comms'
                ? `1em solid ${
                    rfcColors[audience.stagePercentages[0].roleForComms.role]
                  }`
                : `1em solid rgba(var(--rgb-primary), ${columnOpacity[index]})`,
            width: `${scaledPopulations[index]}%`,
            alignSelf: alignments[index % 3],
          })}
        >
          <div class="audience-title">${audience.name}</div>
          <div class="audience-details">
            ${formatAudienceNum(audience.populationCount)}
            ${((audience.idCount / audience.populationCount) * 100).toFixed(2)}%
          </div>
          <div class="audience-buttons">
            <omni-tooltip>
              <div slot="content">
                ${this.audienceView === 'viz'
                  ? 'Manage Custom Segments'
                  : 'Manage Role for Comms'}
              </div>
              <button
                class="button is-text"
                @click=${() => cellBtnClickHandler(index)}
              >
                <omni-icon
                  class="is-size-1"
                  icon-id=${this.audienceView === 'viz'
                    ? 'omni:object:audience'
                    : 'omni:app:performanceReporting'}
                ></omni-icon>
              </button>
            </omni-tooltip>
            <omni-tooltip>
              <div slot="content">View Info</div>
              <button
                class="button is-text"
                @click=${() => this.#openSegmentInfo(index)}
              >
                <omni-icon
                  class="is-size-2"
                  icon-id="omni:informative:info"
                ></omni-icon>
              </button>
            </omni-tooltip>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <omni-style>
        ${this.#addSegmentTemplate} ${this.#segmentInfoTemplate}
        ${this.#rfcSlidersTemplate}
        <div class="cluster-container">
          ${repeat(
            this.audiences,
            (audience, index) => `${audience.label}-${index}`,
            (audience, index) => this.#renderCircle(audience, index),
          )}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register(
  'campaign-audience-cluster-viz',
  CampaignAudienceClusterViz,
);
